import React, { PureComponent } from "react";
import { findDOMNode } from "react-dom";
import EventListener from "react-event-listener";
import ResizeObserver from "resize-observer-polyfill";

import Portal from "../portal/Portal";
import { getElementOffsetDimensions } from "../utils/dimensions";

export interface BaseModalUIProps {
    uiPosition?: "center" | "top";
    uiOffsetLeft?: number;
    uiOffsetTop?: number;
    className?: string;
    uiCloseOnEsc?: boolean;
    uiCloseOnOutsideClick?: boolean;
    uiOnRequestClose(event: Event | React.SyntheticEvent<any>): void;
    uiOnOpen?(): void;
    uiOnClose?(): void;
}

export type BaseModalProps = BaseModalUIProps & {
    children?: React.ReactNode;
};

class BaseModal extends PureComponent<BaseModalProps> {
    public static defaultProps: Partial<BaseModalProps> = {
        uiOffsetLeft: 0,
        uiOffsetTop: 0,
        uiPosition: "center",
        uiCloseOnEsc: true,
        uiCloseOnOutsideClick: true,
    };

    private resizeObserver: ResizeObserver = new ResizeObserver(() => this.updatePosition());
    private modalBodyElement: HTMLElement | undefined = undefined;

    public componentDidMount(): void {
        this.modalBodyElement = findDOMNode(this) as HTMLElement;
        if (this.modalBodyElement) {
            this.resizeObserver.observe(this.modalBodyElement);
        }
        this.updatePosition(true);
    }

    public componentDidUpdate(prevProps: BaseModalProps): void {
        if (prevProps.uiPosition !== this.props.uiPosition) {
            this.updatePosition();
        }
    }

    public componentWillUnmount(): void {
        if (this.modalBodyElement) {
            this.resizeObserver.unobserve(this.modalBodyElement);
            this.modalBodyElement = undefined;
        }
    }

    public render(): JSX.Element | null {
        const {
            uiCloseOnEsc,
            uiCloseOnOutsideClick,
            uiOnClose,
            uiOnOpen,
            className,
            uiOnRequestClose,
            children,
        } = this.props;

        return (
            <Portal
                uiCloseOnEsc={uiCloseOnEsc}
                uiCloseOnOutsideClick={uiCloseOnOutsideClick}
                uiOnOpened={uiOnOpen}
                uiOnClosed={uiOnClose}
                uiOnRequestClose={uiOnRequestClose}
                className={className}
            >
                {children}
                <EventListener key="resize-listener" target="window" onResize={this.onResize} />
            </Portal>
        );
    }

    private updatePosition = (initial: boolean = false): void => {
        if (!this.modalBodyElement) {
            return;
        }
        if (initial) {
            this.modalBodyElement.style.position = "absolute";
            this.modalBodyElement.style.visibility = "hidden";
            this.modalBodyElement.style.top = "0px";
            this.modalBodyElement.style.left = "0px";
        }
        const { height: modalHeight, width: modalWidth } = getElementOffsetDimensions(this.modalBodyElement);
        const style = getComputedStyle(this.modalBodyElement);
        const marginTop = parseInt(style.marginTop || "0", 10) || 0;
        const marginBottom = parseInt(style.marginBottom || "0", 10) || 0;
        const marginLeft = parseInt(style.marginLeft || "0", 10) || 0;
        const marginRight = parseInt(style.marginRight || "0", 10) || 0;

        const { uiPosition, uiOffsetLeft = 0, uiOffsetTop = 0 } = this.props;
        let top = 0;
        let left = (window.innerWidth / 2) - ((modalWidth + marginLeft + marginRight) / 2) + uiOffsetLeft;
        if (left <= 0) {
            left = uiOffsetLeft;
        }
        if (uiPosition === "center") {
            top = (window.innerHeight / 2) - ((modalHeight + marginTop + marginBottom) / 2) + uiOffsetTop;
        }
        if (top <= 0) {
            top = uiOffsetTop;
        }

        this.modalBodyElement.style.visibility = "visible";
        this.modalBodyElement.style.position = "absolute";
        this.modalBodyElement.style.top = `${top}px`;
        this.modalBodyElement.style.left = `${left}px`;
    };

    private onResize = () => {
        this.updatePosition();
    };
}

export default BaseModal;
